import { useEffect, useState } from 'react';
import { Lightning, ArrowRight, CheckCircle, Warning, WarningCircle, Info } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

const SEVERITY_STYLES = {
  critical: {
    pill: 'bg-red-100 text-red-700 border-red-300',
    icon: <WarningCircle size={16} weight="fill" className="text-red-600 shrink-0" />,
    accent: 'border-l-red-500',
  },
  high: {
    pill: 'bg-orange-100 text-orange-700 border-orange-300',
    icon: <Warning size={16} weight="fill" className="text-orange-600 shrink-0" />,
    accent: 'border-l-orange-500',
  },
  medium: {
    pill: 'bg-amber-100 text-amber-700 border-amber-300',
    icon: <Info size={16} weight="fill" className="text-amber-600 shrink-0" />,
    accent: 'border-l-amber-500',
  },
  low: {
    pill: 'bg-blue-100 text-blue-700 border-blue-300',
    icon: <Info size={16} weight="fill" className="text-blue-600 shrink-0" />,
    accent: 'border-l-blue-400',
  },
};

export default function TodaysActionItems() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/dashboard/action-items');
      setItems(data.items || []);
    } catch (e) {
      // silent — fall back to empty list
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-4 animate-pulse" data-testid="action-items-loading">
        <div className="h-5 w-40 bg-gray-200 rounded" />
      </div>
    );
  }

  // Empty (zen) state — show subtle confirmation
  if (items.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3" data-testid="action-items-empty">
        <CheckCircle size={22} weight="fill" className="text-emerald-600" />
        <div>
          <div className="text-sm font-semibold text-emerald-800">All caught up</div>
          <div className="text-xs text-emerald-700">No critical action items right now. Nice work!</div>
        </div>
      </div>
    );
  }

  const totalCount = items.reduce((s, it) => s + (it.count || 0), 0);

  return (
    <div className="bg-gradient-to-br from-violet-50 via-white to-blue-50 border border-violet-200 rounded-lg overflow-hidden" data-testid="todays-action-items">
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/40 transition-colors"
        data-testid="action-items-toggle"
      >
        <div className="flex items-center gap-2">
          <Lightning size={20} weight="fill" className="text-violet-600" />
          <span className="font-semibold text-gray-800">Today's Action Items</span>
          <span className="text-[11px] uppercase tracking-wider bg-violet-600 text-white px-2 py-0.5 rounded-full font-bold">
            {items.length} task{items.length === 1 ? '' : 's'} · {totalCount} item{totalCount === 1 ? '' : 's'}
          </span>
        </div>
        <span className="text-xs text-gray-500">{collapsed ? 'Expand' : 'Collapse'}</span>
      </button>

      {!collapsed && (
        <div className="divide-y divide-violet-100 bg-white/60">
          {items.map((it) => {
            const sev = SEVERITY_STYLES[it.severity] || SEVERITY_STYLES.low;
            return (
              <Link
                key={it.id}
                to={it.link || '#'}
                className={`flex items-center gap-3 px-4 py-2.5 hover:bg-white transition-colors border-l-4 ${sev.accent}`}
                data-testid={`action-item-${it.id}`}
              >
                {sev.icon}
                <span className="text-sm text-gray-700 flex-1 truncate">{it.label}</span>
                <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded border ${sev.pill}`}>
                  {it.count}
                </span>
                <span className="text-xs font-medium text-violet-700 hover:text-violet-900 flex items-center gap-0.5 shrink-0 ml-1">
                  {it.cta || 'Open'} <ArrowRight size={12} />
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
